import { useState, useEffect } from 'react';
import PDFPreview from './PDFPreview';
import ContactSheetContent from './ContactSheetContent';

// Debug flag - set to true to enable colored containers and console logging
const DEBUG_COLORS = false;

/**
 * Unified right pane component that can toggle between:
 * - Single page preview
 * - Contact sheet grid view
 */
function PDFRightPane({
  // Preview props
  pdfFile,
  progress,
  isProcessing,
  error,
  extractedPages,
  currentProcessedPage,
  currentPageIndex,
  isProcessingCurrentPage,
  showOriginal,
  onToggleShowOriginal, // Handler for Show Original toggle
  // Navigation props
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  totalPages,
  // Contact sheet props
  pageExceptions,
  onPageClick,
  onPreservePages,
  processedPagesCache,
  filters,
  getEffectiveFilters,
  onProcessPages,
  savedPresets,
  onApplyPreset,
  // Initial view mode
  initialViewMode = 'preview', // 'preview' or 'contactSheet'
  // Callback to notify parent of view mode changes
  onViewModeChange,
  // Callback to notify parent of selected pages in Contact Sheet mode
  onSelectedPagesChange,
}) {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [showDocumentDetails, setShowDocumentDetails] = useState(false);

  // Notify parent when view mode changes
  useEffect(() => {
    if (onViewModeChange) {
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  // Notify parent when selected pages change
  useEffect(() => {
    if (onSelectedPagesChange) {
      onSelectedPagesChange(selectedPages);
    }
  }, [selectedPages, onSelectedPagesChange]);

  return (
    <div
      className={`flex-1 min-h-0 flex flex-col rounded-lg shadow-lg bg-white dark:bg-gray-800 ${DEBUG_COLORS ? 'bg-red-100' : ''}`}      {...(DEBUG_COLORS && {
        onClick: () => console.log('🔴 RED-100: PDFRightPane Main Container'),
        onTouchStart: () => console.log('🔴 RED-100: PDFRightPane Main Container')
      })}
    >
      {/* View Mode Toggle Header - DEBUG: RED */}
      <div
        className={`sticky top-0 z-10 bg-white dark:bg-gray-800 flex items-center justify-between p-1 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${DEBUG_COLORS ? 'bg-red-200' : ''}`}
        {...(DEBUG_COLORS && {
          onClick: (e) => { e.stopPropagation(); console.log('🔴 RED-200: Single/Multiple/Show Original Header'); },
          onTouchStart: (e) => { e.stopPropagation(); console.log('🔴 RED-200: Single/Multiple/Show Original Header'); }
        })}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 flex-1">
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1.5 rounded-md transition-colors text-xs sm:text-sm font-medium flex items-center gap-1.5 ${
                viewMode === 'preview'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Single Page</span>
              <span className="sm:hidden">Single</span>
            </button>
          <button
            onClick={() => setViewMode('contactSheet')}
            className={`px-3 py-1.5 rounded-md transition-colors text-xs sm:text-sm font-medium flex items-center gap-1.5 ${
              viewMode === 'contactSheet'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            <span className="hidden sm:inline">Multiple Pages</span>
            <span className="sm:hidden">Multiple</span>
          </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Show Original/Hide Originals Toggle - Desktop version */}
          {onToggleShowOriginal && (
            <button
              onClick={onToggleShowOriginal}
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                showOriginal
                  ? 'bg-gray-700 dark:bg-gray-600 text-white'
                  : 'bg-gray-700 dark:bg-gray-600 text-white'
              }`}
              title={viewMode === 'preview' ? (showOriginal ? "Hide Original" : "Show Original") : (showOriginal ? "Hide Originals" : "Show Originals")}
            >
              {/* Stacked boxes icon (box over line over box - like division symbol) */}
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                {/* Top box - farther from line for better spacing */}
                <rect x="4" y="0" width="16" height="8" rx="1" />
                {/* Middle line */}
                <rect x="3" y="11" width="18" height="2" rx="1" />
                {/* Bottom box - farther from line for better spacing */}
                <rect x="4" y="16" width="16" height="8" rx="1" />
              </svg>
              <span>{viewMode === 'preview' ? (showOriginal ? "Hide Original" : "Show Original") : (showOriginal ? "Hide Originals" : "Show Originals")}</span>
            </button>
          )}

          {/* Show Original Toggle - Mobile version */}
          {viewMode === 'preview' && onToggleShowOriginal && (
            <button
              onClick={onToggleShowOriginal}
              className={`md:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                showOriginal
                  ? 'bg-indigo-600 dark:bg-indigo-500 text-white shadow-inner'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 shadow-sm'
              }`}
              title={showOriginal ? "Hide Original" : "Show Original"}
            >
              {/* Stacked boxes icon */}
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                {/* Top box */}
                <rect x="4" y="0" width="16" height="8" rx="1" />
                {/* Middle line */}
                <rect x="3" y="11" width="18" height="2" rx="1" />
                {/* Bottom box */}
                <rect x="4" y="16" width="16" height="8" rx="1" />
              </svg>
              <span>{showOriginal ? "Hide Original" : "Show Original"}</span>
            </button>
          )}

          {/* Document Details Button - Mobile only */}
          <button
            onClick={() => setShowDocumentDetails(true)}
            className="md:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Document Details"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Document Details Overlay - Mobile only */}
      {showDocumentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Document Details</h2>
              <button
                onClick={() => setShowDocumentDetails(false)}
                className="p-1 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* File Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">File Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Filename:</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate ml-2" title={pdfFile?.name}>
                      {pdfFile?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Pages:</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {extractedPages?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">File Size:</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {pdfFile?.size ? `${(pdfFile.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Current Page Info (if in preview mode) */}
              {viewMode === 'preview' && extractedPages?.[currentPageIndex] && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Current Page</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Page Number:</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {currentPageIndex + 1} of {extractedPages.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Resolution:</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {extractedPages[currentPageIndex].width} × {extractedPages[currentPageIndex].height} px
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Page Exceptions Info */}
              {pageExceptions && Object.keys(pageExceptions).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Page Customizations</h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {Object.keys(pageExceptions).filter(key => pageExceptions[key]?.enabled).length} page(s) with custom settings
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDocumentDetails(false)}
                className="w-full py-2 px-4 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 min-h-0 flex flex-col">
        {viewMode === 'preview' ? (
          <PDFPreview
            pdfFile={pdfFile}
            progress={progress}
            isProcessing={isProcessing}
            error={error}
            extractedPages={extractedPages}
            currentProcessedPage={currentProcessedPage}
            currentPageIndex={currentPageIndex}
            isProcessingCurrentPage={isProcessingCurrentPage}
            showOriginal={showOriginal}
            pageExceptions={pageExceptions}
            onToggleShowOriginal={onToggleShowOriginal}
            onFirstPage={onFirstPage}
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onLastPage={onLastPage}
            totalPages={totalPages}
            processedPagesCache={processedPagesCache}
            getEffectiveFilters={getEffectiveFilters}
            filters={filters}
          />
        ) : (
          <ContactSheetContent
            extractedPages={extractedPages}
            pageExceptions={pageExceptions}
            currentPageIndex={currentPageIndex}
            onPageClick={(index) => {
              onPageClick(index);
              setViewMode('preview'); // Auto-switch to preview when clicking a page
            }}
            onPreservePages={onPreservePages}
            processedPagesCache={processedPagesCache}
            filters={filters}
            getEffectiveFilters={getEffectiveFilters}
            onProcessPages={onProcessPages}
            savedPresets={savedPresets}
            onApplyPreset={onApplyPreset}
            selectedPages={selectedPages}
            setSelectedPages={setSelectedPages}
            showOriginal={showOriginal}
            onToggleShowOriginal={onToggleShowOriginal}
          />
        )}
      </div>
    </div>
  );
}

export default PDFRightPane;
